import React from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { BankDetails, PanDetails } from "types/admin";

export interface KycDetailTag {
  key: string;
  label: string;
  value: React.ReactNode;
}

export function NameMatchedIcon({ matched }: { matched: boolean }) {
  const Icon = matched ? CheckCircleOutlineIcon : CancelOutlinedIcon;
  const color = matched ? "success.main" : "error.main";
  const label = matched ? "Name matched" : "Name not matched";

  return (
    <Tooltip title={label}>
      <Icon fontSize="small" sx={{ color, verticalAlign: "middle" }} aria-label={label} />
    </Tooltip>
  );
}

export function KycDetailTags({
  tags,
  action,
}: {
  tags: KycDetailTag[];
  action?: React.ReactNode;
}) {
  if (tags.length === 0 && !action) return null;

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      {tags.map((tag) => (
        <Chip
          key={tag.key}
          size="small"
          variant="outlined"
          sx={{
            height: "auto",
            py: 0.25,
            "& .MuiChip-label": { px: 1, whiteSpace: "normal" },
          }}
          label={
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Typography component="span" variant="caption" color="text.secondary">
                {tag.label}:
              </Typography>
              {typeof tag.value === "string" || typeof tag.value === "number" ? (
                <Typography component="span" variant="caption" fontWeight={600}>
                  {tag.value}
                </Typography>
              ) : (
                tag.value
              )}
            </Box>
          }
        />
      ))}
      {action}
    </Stack>
  );
}

export function buildPanDetailTags(pan?: PanDetails): KycDetailTag[] {
  if (!pan) return [];

  const tags: KycDetailTag[] = [];

  if (pan.panNumber) {
    tags.push({ key: "panNumber", label: "PAN", value: pan.panNumber });
  }
  if (pan.fullName) {
    tags.push({ key: "fullName", label: "Name as per PAN", value: pan.fullName });
  }
  if (pan.panValidityStatus) {
    tags.push({ key: "validity", label: "Validity", value: pan.panValidityStatus });
  }
  if (pan.panOperationalStatus) {
    tags.push({
      key: "operationalStatus",
      label: "Operational Status",
      value: pan.panOperationalStatus,
    });
  }
  if (pan.isCompliant != null) {
    tags.push({
      key: "compliant",
      label: "Compliant",
      value: pan.isCompliant ? "Yes" : "No",
    });
  }

  return tags;
}

export function buildBankDetailTags(bank?: BankDetails): KycDetailTag[] {
  if (!bank) return [];

  const tags: KycDetailTag[] = [];

  if (bank.bankAccountNumber) {
    tags.push({ key: "accountNumber", label: "Account Number", value: bank.bankAccountNumber });
  }
  if (bank.ifscCode) {
    tags.push({ key: "ifsc", label: "IFSC", value: bank.ifscCode });
  }
  if (bank.bankName) {
    tags.push({ key: "bank", label: "Bank", value: bank.bankName });
  }
  if (bank.branchName) {
    tags.push({ key: "branch", label: "Branch", value: bank.branchName });
  }
  if (bank.accountHolderName) {
    tags.push({ key: "accountHolder", label: "Account Holder", value: bank.accountHolderName });
  }
  if (bank.userNameMatchScore != null) {
    tags.push({
      key: "nameMatchScore",
      label: "Name Match Score",
      value: `${bank.userNameMatchScore}%`,
    });
  }
  // Name-match outcome is shown as a stamp on the Bank Account Proof header (NameMatchStamp)

  return tags;
}
